// DEM-2129 — Loops C · Wizard de criação/edição de Loop.
//
// Admin 2026-07-20: só existe loop de AGENTE (skill/pipeline descontinuados).
// Cobre os 3 modos de periodicidade (frequência simples / calendário / personalizada). O mapeamento
// UI→schedule_rrule é DELEGADO ao módulo puro loops-rrule.ts (MUST_FIX #2). O
// PREVIEW de 5 ocorrências (MUST_FIX #1) é resolvido via resolvePreview() —
// backend POST /v1/loops/preview com fallback client-side — e o botão "Salvar"
// FICA BLOQUEADO enquanto o preview não for renderizado (guardrail anti-runaway).

import { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { Alert } from '@/components/ui/Alert';
import { useToast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import type {
  Loop,
  LoopCatchUpPolicy,
  CreateLoopPayload,
} from '@/lib/types/loops';
import type { LoopOutputChannel, AgentListItem } from '@/lib/api';
import {
  buildSchedulePayload,
  resolvePreview,
  simpleIntervalSeconds,
  LOOP_MIN_INTERVAL_SECONDS,
  type LoopSchedule,
  type SimpleFreqUnit,
  type CalendarFreq,
  type CustomKind,
} from '@/lib/loops-rrule';
import { fmtDateTime } from '@/lib/format';

interface LoopWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quando presente, o wizard opera em modo EDIÇÃO pré-preenchido. */
  loop?: Loop | null;
  onSaved: (loop: Loop) => void;
}

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

type PeriodicityMode = 'simple' | 'calendar' | 'custom';

/** Estado inicial do schedule (frequência simples, a cada 1 hora). */
function defaultSchedule(): LoopSchedule {
  return { mode: 'simple', every: 1, unit: 'hours' };
}

/** Re-hidrata o schedule a partir do loop em edição (schedule_display.ui). */
function scheduleFromLoop(loop: Loop | null | undefined): LoopSchedule {
  const ui = (loop?.schedule_display as { ui?: unknown } | undefined)?.ui;
  if (ui && typeof ui === 'object' && 'mode' in (ui as object)) {
    return ui as LoopSchedule;
  }
  return defaultSchedule();
}

export function LoopWizard({ open, onOpenChange, loop, onSaved }: LoopWizardProps) {
  const isEdit = !!loop;
  const { toast } = useToast();

  // ─── Campos base ───────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [targetSlug, setTargetSlug] = useState('');
  const [instruction, setInstruction] = useState('');
  const [outputChannelId, setOutputChannelId] = useState<string>('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [catchUp, setCatchUp] = useState<LoopCatchUpPolicy>('run_once');
  const [budgetDaily, setBudgetDaily] = useState('');

  // ─── Periodicidade ─────────────────────────────────────────────────────────
  const [schedule, setSchedule] = useState<LoopSchedule>(defaultSchedule());

  // ─── Catálogos ─────────────────────────────────────────────────────────────
  const [agents, setAgents] = useState<AgentListItem[]>([]);
  const [channels, setChannels] = useState<LoopOutputChannel[]>([]);

  // ─── Preview (guardrail) ───────────────────────────────────────────────────
  const [preview, setPreview] = useState<Date[] | null>(null);
  const [previewSource, setPreviewSource] = useState<'backend' | 'client' | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [rruleError, setRruleError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reseta/pré-preenche ao abrir.
  useEffect(() => {
    if (!open) return;
    setName(loop?.name ?? '');
    setTargetSlug(loop?.target_slug ?? '');
    const input = (loop?.execution_input ?? {}) as Record<string, unknown>;
    setInstruction(typeof input['instruction'] === 'string' ? input['instruction'] : '');
    setOutputChannelId(loop?.output_channel_connection_id ?? '');
    setStartAt(loop?.start_at ? loop.start_at.slice(0, 16) : '');
    setEndAt(loop?.end_at ? loop.end_at.slice(0, 16) : '');
    setCatchUp(loop?.catch_up_policy ?? 'run_once');
    setBudgetDaily(
      loop?.budget_daily_usd !== null && loop?.budget_daily_usd !== undefined
        ? String(loop.budget_daily_usd)
        : '',
    );
    setSchedule(scheduleFromLoop(loop));
    setPreview(null);
    setPreviewSource(null);
    setRruleError(null);
  }, [open, loop]);

  // Carrega catálogos ao abrir.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [ags, chs] = await Promise.all([
          api.listAgents().catch(() => [] as AgentListItem[]),
          api.listLoopChannels().catch(() => [] as LoopOutputChannel[]),
        ]);
        if (cancelled) return;
        setAgents(ags);
        // Canal de saída = canal de COMUNICAÇÃO conectado (bots Telegram dos
        // companions) — o backend já devolve só canais FUNCIONAIS (token+chat).
        setChannels(chs);
      } catch {
        /* degradação: catálogos vazios */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // A RRULE deriva do schedule via módulo puro (MUST_FIX #2). Invalida preview
  // sempre que a periodicidade ou o start_at mudam (força re-preview p/ salvar).
  const rrule = useMemo(() => {
    try {
      const r = buildSchedulePayload(schedule).schedule_rrule;
      setRruleError(null);
      return r;
    } catch (err) {
      setRruleError(err instanceof Error ? err.message : String(err));
      return null;
    }
  }, [schedule]);

  useEffect(() => {
    // qualquer mudança de rrule/start_at invalida o preview já renderizado.
    setPreview(null);
    setPreviewSource(null);
  }, [rrule, startAt]);

  const runPreview = useCallback(async () => {
    if (!rrule) return;
    setPreviewing(true);
    try {
      const result = await resolvePreview(
        rrule,
        startAt ? new Date(startAt) : null,
        (payload) => api.previewLoopOccurrences(payload),
      );
      setPreview(result.occurrences);
      setPreviewSource(result.source);
    } catch (err) {
      toast({ message: err instanceof Error ? err.message : 'Falha ao calcular preview', type: 'error' });
    } finally {
      setPreviewing(false);
    }
  }, [rrule, startAt, toast]);

  // ─── Validação de "salvar" (guardrail anti-runaway) ────────────────────────
  const previewRendered = preview !== null && preview.length > 0;
  const baseValid =
    name.trim().length > 0 &&
    targetSlug.trim().length > 0 &&
    !rruleError &&
    !!rrule;
  const executionValid = instruction.trim().length > 0;
  const canSave = baseValid && executionValid && previewRendered && !saving;

  const buildPayload = useCallback((): CreateLoopPayload => {
    const schedulePayload = buildSchedulePayload(schedule);
    const executionInput: Record<string, unknown> = { instruction: instruction.trim() };
    const budgetNum = budgetDaily.trim() === '' ? null : Number(budgetDaily);
    return {
      name: name.trim(),
      execution_type: 'agent',
      target_slug: targetSlug.trim(),
      execution_input: executionInput,
      schedule_rrule: schedulePayload.schedule_rrule,
      schedule_display: schedulePayload.schedule_display,
      output_channel_connection_id: outputChannelId || null,
      start_at: startAt ? new Date(startAt).toISOString() : null,
      end_at: endAt ? new Date(endAt).toISOString() : null,
      catch_up_policy: catchUp,
      budget_daily_usd: budgetNum !== null && Number.isNaN(budgetNum) ? null : budgetNum,
    };
  }, [
    schedule, instruction, budgetDaily,
    name, targetSlug, outputChannelId, startAt, endAt, catchUp,
  ]);

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      const saved = isEdit && loop
        ? await api.updateLoop(loop.id, payload)
        : await api.createLoop(payload);
      toast({ message: isEdit ? 'Loop atualizado' : 'Loop criado', type: 'success' });
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast({ message: err instanceof Error ? err.message : 'Falha ao salvar', type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [canSave, buildPayload, isEdit, loop, onSaved, onOpenChange, toast]);

  // ─── Seletores de catálogo ─────────────────────────────────────────────────
  const targetOptions = useMemo(
    () => agents.map((a) => ({ value: a.slug, label: a.name || a.slug })),
    [agents],
  );

  const channelOptions = useMemo(
    () =>
      channels.map((c) => ({
        value: c.id,
        label: `${c.label} · Telegram${c.detail ? ` (${c.detail})` : ''}`,
      })),
    [channels],
  );

  const periodicityMode: PeriodicityMode = schedule.mode;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-h-[90vh] overflow-y-auto" data-testid="loop-wizard">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Loop' : 'Novo Loop'}</DialogTitle>
          <DialogDescription>
            Configure a execução rotineira e revise o preview antes de salvar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Nome */}
          <div className="space-y-1.5">
            <Label htmlFor="loop-name">Nome</Label>
            <Input
              id="loop-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Resumo diário de vendas"
            />
          </div>

          {/* Agente alvo (admin 2026-07-20: só existe loop de agente) */}
          <div className="space-y-3">
            <Select
              label="Agente"
              options={targetOptions}
              value={targetSlug}
              onChange={setTargetSlug}
              placeholder="Selecione o agente"
            />

            <div className="space-y-1.5">
              <Label htmlFor="loop-instruction">Instrução</Label>
              <Textarea
                id="loop-instruction"
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder="O que o agente deve fazer a cada execução"
                rows={3}
              />
            </div>

            {/* Canal de saída — canais de COMUNICAÇÃO conectados (bots dos companions) */}
            <div className="space-y-1.5">
              <Label>Canal de saída (opcional)</Label>
              {channelOptions.length === 0 ? (
                <Alert variant="warning">
                  Nenhum canal de comunicação conectado. O canal de saída é o bot Telegram de um companion — ative um companion com bot pra receber a saída do loop.
                </Alert>
              ) : (
                <Select
                  options={[{ value: '', label: 'Nenhum' }, ...channelOptions]}
                  value={outputChannelId}
                  onChange={setOutputChannelId}
                  placeholder="Selecione o canal de saída"
                />
              )}
              <p className="text-xs text-raios-muted">
                Quando o alvo é um companion, a resposta sai na própria conversa dele.
              </p>
            </div>
          </div>

          {/* Periodicidade */}
          <div className="space-y-3">
            <Label>Periodicidade</Label>
            <div className="flex gap-2">
              {([
                ['simple', 'Frequência simples'],
                ['calendar', 'Calendário'],
                ['custom', 'Personalizada'],
              ] as [PeriodicityMode, string][]).map(([m, label]) => (
                <Button
                  key={m}
                  type="button"
                  variant={periodicityMode === m ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    if (m === 'simple') setSchedule({ mode: 'simple', every: 1, unit: 'hours' });
                    else if (m === 'calendar')
                      setSchedule({ mode: 'calendar', freq: 'daily', byHour: [9], byWeekday: [], byMonthday: [], byYearday: [] });
                    else setSchedule({ mode: 'custom', kind: 'every_n_days', interval: 2 });
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>

            {schedule.mode === 'simple' && (
              <div className="flex items-end gap-2">
                <div className="w-24 space-y-1.5">
                  <Label htmlFor="freq-every">A cada</Label>
                  <Input
                    id="freq-every"
                    type="number"
                    min={1}
                    value={schedule.every}
                    onChange={(e) =>
                      setSchedule({ ...schedule, every: Math.max(1, Number(e.target.value) || 1) })
                    }
                  />
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label>Unidade</Label>
                  <Select
                    options={[
                      { value: 'seconds', label: 'Segundos' },
                      { value: 'minutes', label: 'Minutos' },
                      { value: 'hours', label: 'Horas' },
                      { value: 'days', label: 'Dias' },
                    ]}
                    value={schedule.unit}
                    onChange={(v) => setSchedule({ ...schedule, unit: v as SimpleFreqUnit })}
                  />
                </div>
              </div>
            )}

            {schedule.mode === 'simple' &&
              simpleIntervalSeconds(schedule) < LOOP_MIN_INTERVAL_SECONDS && (
                <Alert variant="warning">
                  O intervalo mínimo é {LOOP_MIN_INTERVAL_SECONDS}s.
                </Alert>
              )}

            {schedule.mode === 'calendar' && (
              <div className="space-y-3">
                <Select
                  label="Frequência"
                  options={[
                    { value: 'daily', label: 'Diária' },
                    { value: 'weekly', label: 'Semanal' },
                    { value: 'monthly', label: 'Mensal' },
                    { value: 'yearly', label: 'Anual' },
                  ]}
                  value={schedule.freq}
                  onChange={(v) => setSchedule({ ...schedule, freq: v as CalendarFreq })}
                />
                <div className="space-y-1.5">
                  <Label htmlFor="cal-hour">Hora(s) do dia (0–23, separadas por vírgula)</Label>
                  <Input
                    id="cal-hour"
                    value={schedule.byHour.join(',')}
                    onChange={(e) =>
                      setSchedule({
                        ...schedule,
                        byHour: parseIntList(e.target.value, 0, 23),
                      })
                    }
                    placeholder="9"
                  />
                </div>
                {schedule.freq === 'weekly' && (
                  <div className="space-y-1.5">
                    <Label>Dias da semana</Label>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAYS.map((d) => {
                        const active = schedule.byWeekday.includes(d.value);
                        return (
                          <Button
                            key={d.value}
                            type="button"
                            size="sm"
                            variant={active ? 'default' : 'outline'}
                            onClick={() =>
                              setSchedule({
                                ...schedule,
                                byWeekday: active
                                  ? schedule.byWeekday.filter((x) => x !== d.value)
                                  : [...schedule.byWeekday, d.value],
                              })
                            }
                          >
                            {d.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {schedule.freq === 'monthly' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="cal-md">Dia(s) do mês (1–31)</Label>
                    <Input
                      id="cal-md"
                      value={schedule.byMonthday.join(',')}
                      onChange={(e) =>
                        setSchedule({ ...schedule, byMonthday: parseIntList(e.target.value, 1, 31) })
                      }
                      placeholder="1,15"
                    />
                  </div>
                )}
                {schedule.freq === 'yearly' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="cal-yd">Dia(s) do ano (1–366)</Label>
                    <Input
                      id="cal-yd"
                      value={schedule.byYearday.join(',')}
                      onChange={(e) =>
                        setSchedule({ ...schedule, byYearday: parseIntList(e.target.value, 1, 366) })
                      }
                      placeholder="100"
                    />
                  </div>
                )}
              </div>
            )}

            {schedule.mode === 'custom' && (
              <div className="space-y-3">
                <Select
                  label="Regra personalizada"
                  options={[
                    { value: 'every_n_days', label: 'A cada N dias' },
                    { value: 'day_x_every_n_months', label: 'Dia X a cada N meses' },
                    { value: 'first_business_day', label: 'Todo primeiro dia útil do mês' },
                  ]}
                  value={schedule.kind}
                  onChange={(v) => {
                    const kind = v as CustomKind;
                    if (kind === 'every_n_days') setSchedule({ mode: 'custom', kind, interval: 2 });
                    else if (kind === 'day_x_every_n_months')
                      setSchedule({ mode: 'custom', kind, interval: 1, monthday: 1 });
                    else setSchedule({ mode: 'custom', kind });
                  }}
                />
                {schedule.kind === 'every_n_days' && (
                  <div className="w-32 space-y-1.5">
                    <Label htmlFor="cust-n">A cada N dias</Label>
                    <Input
                      id="cust-n"
                      type="number"
                      min={1}
                      value={schedule.interval ?? 1}
                      onChange={(e) =>
                        setSchedule({ ...schedule, interval: Math.max(1, Number(e.target.value) || 1) })
                      }
                    />
                  </div>
                )}
                {schedule.kind === 'day_x_every_n_months' && (
                  <div className="flex gap-2">
                    <div className="w-28 space-y-1.5">
                      <Label htmlFor="cust-day">Dia do mês</Label>
                      <Input
                        id="cust-day"
                        type="number"
                        min={1}
                        max={31}
                        value={schedule.monthday ?? 1}
                        onChange={(e) =>
                          setSchedule({
                            ...schedule,
                            monthday: Math.min(31, Math.max(1, Number(e.target.value) || 1)),
                          })
                        }
                      />
                    </div>
                    <div className="w-28 space-y-1.5">
                      <Label htmlFor="cust-months">A cada N meses</Label>
                      <Input
                        id="cust-months"
                        type="number"
                        min={1}
                        value={schedule.interval ?? 1}
                        onChange={(e) =>
                          setSchedule({
                            ...schedule,
                            interval: Math.max(1, Number(e.target.value) || 1),
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Datas de início/encerramento (opcionais) */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="start-at">Início (vazio = imediato)</Label>
              <Input
                id="start-at"
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-at">Encerramento (vazio = sem fim)</Label>
              <Input
                id="end-at"
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
              />
            </div>
          </div>

          {/* Avançado */}
          <div className="space-y-3 rounded-lg border border-raios-border p-3">
            <Label>Avançado</Label>
            <div className="flex items-center justify-between">
              <span className="text-sm text-raios-text-secondary">
                Executar disparos perdidos (catch-up)
              </span>
              <Switch
                checked={catchUp === 'run_once'}
                onCheckedChange={(v) => setCatchUp(v ? 'run_once' : 'skip')}
                aria-label="Catch-up"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="budget">Orçamento diário USD (vazio = ilimitado)</Label>
              <Input
                id="budget"
                type="number"
                min={0}
                step="0.01"
                value={budgetDaily}
                onChange={(e) => setBudgetDaily(e.target.value)}
                placeholder="Ilimitado"
              />
            </div>
          </div>

          {/* PREVIEW — guardrail anti-runaway (MUST_FIX #1) */}
          <div className="space-y-2 rounded-lg border border-raios-border p-3" data-testid="loop-preview-section">
            <div className="flex items-center justify-between">
              <Label>Próximas 5 ocorrências</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                loading={previewing}
                disabled={!rrule || previewing}
                onClick={runPreview}
                data-testid="loop-preview-btn"
              >
                Calcular preview
              </Button>
            </div>
            {rruleError && <Alert variant="destructive">{rruleError}</Alert>}
            {previewRendered ? (
              <ul className="space-y-1 text-sm" data-testid="loop-preview-list">
                {preview!.map((d, i) => (
                  <li key={i} className="text-raios-text-secondary" data-testid="loop-preview-item">
                    {fmtDateTime(d)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-raios-muted">
                Calcule o preview para revisar as próximas execuções antes de salvar.
              </p>
            )}
            {previewSource === 'client' && previewRendered && (
              <p className="text-xs text-raios-muted">Calculado localmente (fallback rrule).</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!canSave} loading={saving} data-testid="loop-save-btn">
            {isEdit ? 'Salvar alterações' : 'Criar Loop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Parse de lista de inteiros "1,15" filtrando ao range [min,max]. */
function parseIntList(raw: string, min: number, max: number): number[] {
  return raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isInteger(n) && n >= min && n <= max);
}
